import { RollingValue } from '~/design-system/elements/RollingValue/RollingValue';
import { cn } from '~/lib/utils';
import { BillingCtaButton } from '~/design-system/components/v1_5/Settings';
import type { CreditPack } from '~/queries/subscription';
/*
 * `Settings/BillingCreditSlider`, not this folder's own `CreditSlider`.
 *
 * The old one is built on the LEGACY ramp — `--color-default`, `--color-basic-gray-alpha-10`,
 * `--color-state-primary` — which the appearance work deliberately left light-only (see the note in
 * `root.tsx`). On the redesigned billing page that ramp does not follow the theme, so the slider
 * painted in light colours on a dark card and read as a component borrowed from another product.
 * The v1.5 one is on `--sv15-*` / `--hv15-*` and themes with the page around it.
 *
 * Its API is by VALUE rather than by index, which is the honest shape: the ladder is the stops and
 * the selection is one of them. The index round-trip stays here, in the adapter, because the cards
 * either side of it still speak indices.
 */
import { BillingCreditSlider } from '~/design-system/components/v1_5/Settings';
import { NUMBERFLOW_TIMING } from './pricingContent';
import styles from './PricingTable.module.css';
import local from './TopUpCardUI.module.css';

const formatPrice = (amountCents: number, currency: string): string => {
  const amount = amountCents / 100;
  const value = Number.isInteger(amount) ? amount.toFixed(0) : amount.toFixed(2);
  return currency?.toUpperCase() === 'USD' ? `$${value}` : `${value} ${currency?.toUpperCase() ?? ''}`.trim();
};

export interface TopUpCardUIProps {
  /** Packs sorted low → high by credits. */
  packs: CreditPack[];
  selectedIndex: number;
  onSelectedIndexChange: (index: number) => void;
  onBuy: () => void;
  buying?: boolean;
  /** Vertical layout for narrow slots (e.g. the landing pricing column). */
  stacked?: boolean;
  /** Which skin the buy button wears. Pinned by the caller, not read from the app's appearance —
   *  see the note on `GeneralPlanCardProps.theme`, which this mirrors. */
  theme?: 'light' | 'dark';
}

/**
 * Presentational "Need more credits?" card — the exact billing-page markup with all
 * data and checkout behavior lifted out, so it renders anywhere (billing page with
 * live Stripe packs, landing page with static ones). 1:1 of Figma node 514:7651.
 */
export function TopUpCardUI({ packs, selectedIndex, onSelectedIndexChange, onBuy, buying = false, stacked = false, theme = 'light' }: TopUpCardUIProps) {
  if (packs.length === 0) return null;

  const index = Math.min(selectedIndex, packs.length - 1);
  const selected = packs[index];
  const sliderValues = packs.map((p) => p.credits);

  return (
    <div className={cn(styles.generalCardWrap, stacked && local.wrapFill)}>
      <div className={cn(styles.generalCard, stacked && local.cardStacked)}>
        {/* Left: title + subtitle */}
        <div className={cn(styles.featuresCol, stacked && local.colStacked)}>
          <div className={styles.planTitleBlock}>
            <div className={styles.planTitle}>Need more credits?</div>
            <p className={styles.planSubtitle}>Top up anytime. Purchased credits never expire.</p>
          </div>
        </div>

        {!stacked && <div className={styles.cardDivider} aria-hidden />}

        {/* Right: label, price + slider, buy button */}
        <div className={cn(styles.buyCol, stacked && local.colStacked)}>
          <div className={styles.buyControls}>
            <div className={styles.buyMoreLabel}>Buy Credits</div>
            <div className={styles.priceSliderGroup}>
              <div className={styles.planPriceRow}>
                <span className={styles.planPriceAmount}>
                  {/* The whole string, not the number with a `suffix` prop — Scritto has none, and
                      does not need one: it diffs glyph for glyph, so " Credits" simply does not
                      change and therefore does not move. Only the digits roll. */}
                  <RollingValue value={`${selected.credits.toLocaleString('en-US')} Credits`} transition={NUMBERFLOW_TIMING} />
                </span>
                <span className={styles.planPriceRight}>
                  {selected.currency?.toUpperCase() === 'USD' ? (
                    <RollingValue value={formatPrice(selected.amountCents, 'USD')} transition={NUMBERFLOW_TIMING} />
                  ) : (
                    formatPrice(selected.amountCents, selected.currency)
                  )}
                </span>
              </div>
              <BillingCreditSlider
                stops={sliderValues}
                value={sliderValues[index] ?? sliderValues[0]}
                onChange={(next) => onSelectedIndexChange(sliderValues.indexOf(next))}
                label="Credits per month"
              />
            </div>
            {/* `soft`, which is exactly what `TopUpCard` — the signed-in twin of this card — draws
                for the same action. Buying credits is secondary to choosing a plan, and `key` is
                reserved for the one button that changes what you pay every month. */}
            <BillingCtaButton
              skin="soft"
              className={styles.ctaInline}
              theme={theme}
              onClick={onBuy}
              loading={buying}
              disabled={buying}
            >
              Buy Credits
            </BillingCtaButton>
          </div>
        </div>
      </div>
    </div>
  );
}
